import React, { useState, useEffect } from "react";
import Validate from "app/validation/validation";
import { useSelector, useDispatch } from "react-redux";
import { executeWithCaptcha, getMediaPath } from "../../app/functions";
import AnalyticsManager from "../../app/analytics";
import "./membershipPage.scss";
import * as Actions from "../../redux/actions";
import * as Popups from "../../constants/popup-types";
//components
import AnimatedInput from "components/forms/animated_input";
import Recaptcha from "../../components/services/google/recaptcha";
import ImageHeader from "../../components/image-header";
import {
  useGetMembershipGeneratorPageQuery,
  useGetClubMemberCertificateMutation,
} from "../../redux/api/relations.api";

function MembershipCheckPage() {
  const dispatch = useDispatch();
  const deviceState = useSelector((store) => store.state.deviceState);
  const userData = useSelector((store) => store.config.UserData);
  const text = useSelector((store) => store.config.Translations);
  const [firstTry, setFirstTry] = useState(true);
  const [form, setForm] = useState({
    id: { val: "", valid: false, rules: ["not_empty", "id"], errMsg: "" },
    email: { val: "", valid: false, rules: ["should_add_email_if_not_exists"], errMsg: "" },
    last_digits: {
      val: "",
      valid: false,
      rules: ["not_empty", "last_digits"],
      errMsg: "",
    },
    full_name: { val: "", valid: false, rules: ["not_empty", 'full_name'], errMsg: "" },
  });

  const { data: membershipPageData } = useGetMembershipGeneratorPageQuery();
  const [membershipCheckPage, setMembershipCheckPage] =
    useState(membershipPageData);

  const [responseMessage, setResponseMessage] = useState(null);
  const [
    getClubMemberCertificate,
    { isLoading: isLoadingClubMemberCertificate },
  ] = useGetClubMemberCertificateMutation();

  const [formStatus, setFormStatus] = useState(false);

  useEffect(() => {
    if (membershipPageData) {
      setMembershipCheckPage(membershipPageData);
    }
  }, [membershipPageData]);

  useEffect(() => {
    if (userData) {
      handleUserLoggedIn(userData);
    }
  }, [userData]);

  useEffect(() => {
    if (formStatus) {
      AnalyticsManager.logRequestMembershipCard();
    }
  }, [formStatus]);

  const handleUserLoggedIn = async (user) => {
    await handleInput({
      target: {
        name: "full_name",
        value: `${user.first_name} ${user.last_name}`,
      },
    });
    await handleInput({ target: { name: "id", value: `${user.user_id}` } });
    await handleInput({ target: { name: "email", value: `${user.email}` } });
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    let validate = Validate(value, form[name].rules);
    let is_valid = validate.valid;
    let msg = validate.msg;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: { ...prevForm[name], val: value, valid: is_valid, errMsg: msg },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!userData) {
      dispatch(Actions.addPopup({
        type: Popups.LOGIN,
        text: text?.['login_to_check_membership'],
      }));
      return;
    }
    let form_valid = true;
    let new_state = { ...form };

    for (let field in form) {
      let validate = Validate(form[field].val, form[field].rules);
      let is_valid = validate.valid;
      let msg = validate.msg;

      new_state[field].valid = is_valid;
      new_state[field].errMsg = msg;

      if (is_valid === false) {
        form_valid = false;
      }
    }
    setForm(new_state);
    setFirstTry(false);

    if (form_valid) {
      executeWithCaptcha(async (token) => {
        let payload = {};
        payload["userId"] = userData.user_id;
        payload["userName"] = `${userData?.first_name} ${userData?.last_name}`;
        payload["email"] = userData.email;
        payload["g-recaptcha-response"] = token;
        await handleClubMemberCertificate(payload);
      });
    }
  };

  const handleClubMemberCertificate = async (payload) => {
    try {
      const response = await getClubMemberCertificate(payload);
      if (response.data && response.data.validBool) {
        setFormStatus(true);
        setResponseMessage(membershipCheckPage.successMessage);
      } else if (response.data && !response.data.validBool) {
        setFormStatus(false);
        setResponseMessage(response.data.returnValue);
      }
    } catch (error) {
      console.error("Error fetching club member certificate:", error);
    }
  };

  const getClassNames = () => {
    return userData ? "phone-input animate" : "phone-input";
  };

  const isMobile = deviceState === "mobile";
  const showNameErrorMsg = !firstTry && !form.full_name.valid;
  const showIdErrorMsg = !firstTry && !form.id.valid;
  const showEmailErrorMsg = !firstTry && !form.email.valid;

  return (
    <section className="membership-check">
      {membershipCheckPage && (
        <>
          <ImageHeader
            image={
              isMobile
                ? getMediaPath(membershipCheckPage.mobileImagePath)
                : getMediaPath(membershipCheckPage.imagePath)
            }
            title={membershipCheckPage.bannerHeader}
            subtitle={membershipCheckPage.bannerSubHeader}
          />

          <div className="instructions">
            <div className="title-wrapper">
              <h3 className="title">{membershipCheckPage.header}</h3>
              {!formStatus && (
                <span className="sub-title">
                  {userData ? membershipCheckPage.subHeader : text?.['login_to_check_membership']}
                </span>
              )}
            </div>
          </div>

          {!formStatus && !responseMessage && (
            <form onSubmit={handleSubmit}>
              {userData ? (
                <>
                  <AnimatedInput
                    type="text"
                    name="full_name"
                    className={getClassNames()}
                    placeholder={membershipCheckPage.textFieldName}
                    value={form.full_name.val}
                    onChange={handleInput}
                    showError={showNameErrorMsg}
                    errorMessage={form.full_name.errMsg}
                  />

                  <AnimatedInput
                    type="number"
                    name="id"
                    className={getClassNames()}
                    placeholder={membershipCheckPage.textFieldId}
                    value={form.id.val}
                    disabled={true}
                    onChange={handleInput}
                    showError={showIdErrorMsg}
                    errorMessage={form.id.errMsg}
                    pattern={"[0-9]*"}
                  />

                  <AnimatedInput
                    type="text"
                    name="email"
                    className={getClassNames()}
                    placeholder={membershipCheckPage.textFieldEmail}
                    value={form.email.val}
                    disabled={true}
                    onChange={handleInput}
                    showError={showEmailErrorMsg}
                    errorMessage={form.email.errMsg}
                  />

                  <Recaptcha isFunction={false} enterprise={true} />
                </>
              ) : null}

              <button type="submit" className="submit-button">
                {membershipCheckPage.btnText  }
              </button>

              <span className="error-massage">{responseMessage}</span>
            </form>
          )}
          {formStatus && (
            <div className="success_msg">
              {responseMessage}
            </div>
          )}
          {!formStatus && <div className="success_msg">{responseMessage}</div>}
        </>
      )}
    </section>
  );
}

export default MembershipCheckPage;
